import { useCallback, useEffect, useState } from 'react';
import {
  ListRadarDirectionsCommand,
  RadarDirectionStatus,
  getIvtClient,
  type ListRadarDirectionsOutput,
} from '../../client/IvtHubClient';
import { type DenialKind, denialKindOf } from '../../utils/authorization';

export interface RadarDirectionsState {
  data: ListRadarDirectionsOutput | null;
  loading: boolean;
  error: string | null;
  /** Mirrored from useAssetFlow: a 403 must not offer a retry button. */
  denial: DenialKind | null;
  reload: () => void;
}

/**
 * ListRadarDirections for the STO Dashboard's radar section (DAT-9115, R17).
 *
 * One request per industry, scoped to `status=CANDIDATE` — the dashboard only
 * shows live directions; FALSE_POSITIVE / ADOPTED rows belong to audit views
 * that R17 does not build. The handler also folds the three dashboard summary
 * tiles into this one call (induction §5.1.1, topic-model §3.4.1), so the
 * section never needs a second round trip.
 *
 * `industry` null = STO industry scope has not resolved yet (the permitted-
 * industries hook is still loading, failed, or empty); nothing is sent and
 * `loading` stays raised while `enabled`. Mirrors useAssetFlow's shape so the
 * STO view's three radar / asset-flow / account-pipeline hooks all branch the
 * same way at render time.
 *
 * `enabled` flips with the view switch (STO view only); the GM view never
 * fetches, so flipping away cancels any in-flight settle and clears
 * `loading`. Both the asset-flow hook and this one keep the previous
 * response on screen through a transient failure and DROP it on a denial —
 * see the comment in useAssetFlow for why clearing `denial` at request
 * start was the wrong half of that pair.
 */
export function useRadarDirections(industry: string | null, enabled = true): RadarDirectionsState {
  const [data, setData] = useState<ListRadarDirectionsOutput | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [denial, setDenial] = useState<DenialKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    if (industry === null) return;
    (async () => {
      try {
        const out = await getIvtClient().send(
          new ListRadarDirectionsCommand({
            industry,
            // CANDIDATE only; audit views (if they land) pass the other two
            // values. Omitting the field returns all three statuses, which
            // would mix dismissed/adopted rows into the dashboard.
            status: RadarDirectionStatus.CANDIDATE,
          }),
        );
        if (!cancelled) {
          setData(out);
          setDenial(null);
        }
      } catch (e) {
        if (!cancelled) {
          const kind = denialKindOf(e);
          setDenial(kind);
          if (kind !== null) setData(null);
          setError((e as Error).message ?? 'Unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industry, reloadKey, enabled]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, denial, reload };
}
